import { CircleHelp, Info, LogIn, LogOut, MessageCircle, PanelLeftClose, Plus, Settings, Trash2, UserPlus, X } from 'lucide-react';
import type { Conversation } from '../types/chat';
import { LogoMark } from './LogoMark';

export type SidebarUser = {
  name: string;
  email: string;
  avatarUrl?: string;
};

type SidebarProps = {
  conversations: Conversation[];
  activeId: string;
  online: boolean;
  mobileOpen: boolean;
  desktopOpen: boolean;
  hasMessages: boolean;
  user?: SidebarUser | null;
  onCloseMobile: () => void;
  onCloseDesktop: () => void;
  onCreate: () => void;
  onSelect: (id: string) => void;
  onReset: () => void;
  onLogin: () => void;
  onRegister: () => void;
  onLogout?: () => void;
  onAdmin: () => void;
};

function RelativeTime({ timestamp }: { timestamp: number }) {
  const delta = Date.now() - timestamp;
  if (delta < 60_000) return <>Baru saja</>;
  if (delta < 3_600_000) return <>{Math.max(1, Math.floor(delta / 60_000))}m</>;
  if (delta < 86_400_000) return <>{Math.floor(delta / 3_600_000)}j</>;
  return <>{Math.floor(delta / 86_400_000)}h</>;
}

function AccountSection({ user, onLogin, onRegister, onLogout }: Pick<SidebarProps, 'user' | 'onLogin' | 'onRegister' | 'onLogout'>) {
  if (user) {
    const initials = user.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');

    return (
      <div className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-surface-raised p-3">
        {user.avatarUrl ? (
          <img className="size-9 shrink-0 rounded-full object-cover" src={user.avatarUrl} alt={user.name} />
        ) : (
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent-hover" aria-hidden="true">
            {initials || 'AI'}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <strong className="block truncate text-xs font-semibold text-primary">{user.name}</strong>
          <span className="mt-0.5 block truncate text-[11px] text-muted">{user.email}</span>
        </span>
        {onLogout && (
          <button type="button" onClick={onLogout} className="icon-button !size-8" aria-label="Keluar dari akun">
            <LogOut className="size-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-border bg-surface-raised p-3">
      <p className="text-xs font-medium text-secondary">Simpan dan sinkronkan percakapanmu.</p>
      <p className="mt-1 text-[11px] leading-[1.45] text-muted">Masuk atau buat akun untuk mengaksesnya di perangkat lain.</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" onClick={onLogin} className="interactive flex h-9 items-center justify-center gap-2 rounded-lg bg-accent text-xs font-medium text-white hover:bg-accent-fill-hover">
          <LogIn className="size-3.5" />Masuk
        </button>
        <button type="button" onClick={onRegister} className="interactive flex h-9 items-center justify-center gap-2 rounded-lg border border-border text-xs font-medium text-secondary hover:bg-surface-hover hover:text-primary">
          <UserPlus className="size-3.5" />Daftar
        </button>
      </div>
    </div>
  );
}

export function Sidebar({ conversations, activeId, online, mobileOpen, desktopOpen, hasMessages, user = null, onCloseMobile, onCloseDesktop, onCreate, onSelect, onReset, onLogin, onRegister, onLogout, onAdmin }: SidebarProps) {
  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/70 transition-opacity duration-panel lg:hidden ${mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onCloseMobile}
        aria-hidden="true"
      />
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[320px] max-w-[82vw] flex-col border-r border-border bg-canvas px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] transition-transform duration-panel lg:static lg:z-auto lg:w-60 lg:max-w-none lg:translate-x-0 xl:w-[264px] ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} ${desktopOpen ? 'lg:flex' : 'lg:hidden'}`}
        aria-label="Navigasi percakapan"
      >
        <div className="flex h-10 items-center gap-2.5">
          <LogoMark />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">Multi-Model AI</span>
          <button className="icon-button !hidden lg:!grid" type="button" onClick={onCloseDesktop} aria-label="Tutup sidebar">
            <PanelLeftClose className="size-4" />
          </button>
          <button className="icon-button lg:!hidden" type="button" onClick={onCloseMobile} aria-label="Tutup menu">
            <X className="size-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            onCreate();
            onCloseMobile();
          }}
          className="interactive mt-6 flex h-11 w-full items-center gap-3 rounded-xl border border-border bg-surface-raised px-4 text-sm font-medium text-primary hover:bg-surface-hover"
        >
          <Plus className="size-4" />
          Chat baru
        </button>

        <div className="mt-7 min-h-0 flex-1">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted">Percakapan lokal</p>
          <nav className="max-h-full space-y-1 overflow-y-auto pr-1">
            {conversations.map((conversation) => {
              const active = conversation.id === activeId;
              return (
                <button
                  type="button"
                  key={conversation.id}
                  onClick={() => {
                    onSelect(conversation.id);
                    onCloseMobile();
                  }}
                  className={`interactive group relative flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm ${active ? 'bg-accent-soft text-primary' : 'text-secondary hover:bg-surface-hover hover:text-primary'}`}
                >
                  {active && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-accent" />}
                  <MessageCircle className={`size-4 shrink-0 ${active ? 'text-accent-hover' : 'text-muted'}`} />
                  <span className="min-w-0 flex-1 truncate">{conversation.title}</span>
                  <span className="shrink-0 text-[11px] text-muted opacity-0 group-hover:opacity-100 sm:opacity-100">
                    <RelativeTime timestamp={conversation.updatedAt} />
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="border-t border-border pt-3">
          {hasMessages && (
            <button type="button" onClick={onReset} className="sidebar-link text-danger lg:!hidden"><Trash2 className="size-4" />Hapus percakapan</button>
          )}
          <button type="button" onClick={onAdmin} className="sidebar-link"><Settings className="size-4" />Admin project & API key</button>
          <button type="button" className="sidebar-link"><CircleHelp className="size-4" />Pusat Bantuan</button>
          <div className="mt-2 rounded-xl bg-surface-raised p-3 text-xs text-muted">
            <div className="flex items-center gap-2"><Info className="size-3.5" />Tersimpan di browser ini</div>
            <div className={`mt-2 flex items-center gap-2 ${online ? 'text-success' : 'text-warning'}`}>
              <span className="size-2 rounded-full bg-current" />{online ? 'Online' : 'Koneksi tidak stabil'}
            </div>
          </div>
          <AccountSection user={user} onLogin={onLogin} onRegister={onRegister} onLogout={onLogout} />
        </div>
      </aside>
    </>
  );
}
